import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PurchasesNav from '../components/PurchasesNav';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, InputAdornment, Chip, Stack,
  CircularProgress, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Autocomplete, Alert, Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import {
  supplierDirectoryService,
  rawMaterialService,
  dyeService,
  packagingMaterialService,
  filamentService,
  consumableMaterialService,
} from '../services/api';

const CATEGORIES = ['Усі', 'Сировина', 'Барвники', 'Пакувальні матеріали', 'Філамент', 'Розхідні'];

const CAT_COLOR = {
  'Сировина': 'primary',
  'Барвники': 'secondary',
  'Пакувальні матеріали': 'warning',
  'Філамент': 'success',
  'Розхідні': 'info',
};

// Fetch all items from a paginated or plain endpoint
async function fetchAll(service) {
  const res = await service.getAll();
  return Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
}

function SupplierJournalPage() {
  const [loading, setLoading] = useState(true);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [dyes, setDyes] = useState([]);
  const [packaging, setPackaging] = useState([]);
  const [filaments, setFilaments] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [catFilter, setCatFilter] = useState('Усі');
  const [supSearch, setSupSearch] = useState('');
  const [matSearch, setMatSearch] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null); // { mat, category, linkedSuppliers }
  const [editForm, setEditForm] = useState({ name: '', supplier_name: '' });
  const [editSuppliers, setEditSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [rm, dy, pk, fl, sups, cm] = await Promise.all([
        fetchAll(rawMaterialService),
        fetchAll(dyeService),
        fetchAll(packagingMaterialService),
        fetchAll(filamentService),
        fetchAll(supplierDirectoryService),
        fetchAll(consumableMaterialService),
      ]);
      setRawMaterials(rm);
      setDyes(dy);
      setPackaging(pk);
      setFilaments(fl);
      setConsumables(cm);
      setSuppliers(sups);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Build reverse map: category+id -> [supplier]
  const supplierByMat = useMemo(() => {
    const map = {}; // key: `cat:id`
    suppliers.forEach((s) => {
      const link = (arr, cat) => (arr || []).forEach((m) => {
        const id = typeof m === 'object' ? m.id : m;
        const key = `${cat}:${id}`;
        if (!map[key]) map[key] = [];
        map[key].push(s);
      });
      link(s.raw_materials, 'Сировина');
      link(s.dyes, 'Барвники');
      link(s.packaging_materials, 'Пакувальні матеріали');
      link(s.filaments, 'Філамент');
      link(s.consumable_materials, 'Розхідні');
    });
    return map;
  }, [suppliers]);

  // Flat list of all materials with category
  const allRows = useMemo(() => [
    ...rawMaterials.map((m) => ({ mat: m, category: 'Сировина' })),
    ...dyes.map((m) => ({ mat: m, category: 'Барвники' })),
    ...packaging.map((m) => ({ mat: m, category: 'Пакувальні матеріали' })),
    ...filaments.map((m) => ({ mat: m, category: 'Філамент' })),
    ...consumables.map((m) => ({ mat: m, category: 'Розхідні' })),
  ], [rawMaterials, dyes, packaging, filaments, consumables]);

  const filtered = useMemo(() => {
    const sup = supSearch.trim().toLowerCase();
    const mat = matSearch.trim().toLowerCase();
    return allRows.filter(({ mat: m, category }) => {
      if (catFilter !== 'Усі' && category !== catFilter) return false;
      if (mat) {
        const inName = (m.name || '').toLowerCase().includes(mat);
        const inSupName = (m.supplier_name || '').toLowerCase().includes(mat);
        if (!inName && !inSupName) return false;
      }
      if (sup) {
        const key = `${category}:${m.id}`;
        const linked = supplierByMat[key] || [];
        if (!linked.some((s) => s.name.toLowerCase().includes(sup))) return false;
      }
      return true;
    });
  }, [allRows, catFilter, matSearch, supSearch, supplierByMat]);

  const openEdit = (mat, category) => {
    const key = `${category}:${mat.id}`;
    const linked = supplierByMat[key] || [];
    setEditItem({ mat, category });
    setEditForm({ name: mat.name || '', supplier_name: mat.supplier_name || '' });
    setEditSuppliers(linked);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editItem) return;
    const { mat, category } = editItem;
    setSaving(true);
    try {
      // 1. Update material fields
      const service = category === 'Сировина' ? rawMaterialService
        : category === 'Барвники' ? dyeService
        : category === 'Пакувальні матеріали' ? packagingMaterialService
        : category === 'Розхідні' ? consumableMaterialService
        : filamentService;
      await service.patch(mat.id, {
        name: editForm.name,
        supplier_name: editForm.supplier_name,
      });

      // 2. Compute supplier diff
      const key = `${category}:${mat.id}`;
      const prevSupIds = new Set((supplierByMat[key] || []).map((s) => s.id));
      const newSupIds = new Set(editSuppliers.map((s) => s.id));

      const m2mField = category === 'Сировина' ? 'raw_materials'
        : category === 'Барвники' ? 'dyes'
        : category === 'Пакувальні матеріали' ? 'packaging_materials'
        : category === 'Розхідні' ? 'consumable_materials'
        : 'filaments';

      // Suppliers to add mat to
      const toAdd = editSuppliers.filter((s) => !prevSupIds.has(s.id));
      // Suppliers to remove mat from
      const toRemove = (supplierByMat[key] || []).filter((s) => !newSupIds.has(s.id));

      // Fetch full supplier data and patch
      const patchSupplier = async (sup, addMat) => {
        const res = await supplierDirectoryService.getById(sup.id);
        const current = res.data[m2mField] || [];
        const currentIds = current.map((x) => (typeof x === 'object' ? x.id : x));
        let updated;
        if (addMat) {
          updated = [...new Set([...currentIds, mat.id])];
        } else {
          updated = currentIds.filter((id) => id !== mat.id);
        }
        await supplierDirectoryService.patch(sup.id, { [m2mField]: updated });
      };

      await Promise.all([
        ...toAdd.map((s) => patchSupplier(s, true)),
        ...toRemove.map((s) => patchSupplier(s, false)),
      ]);

      setSnackbar({ open: true, message: 'Збережено', severity: 'success' });
      setEditOpen(false);
      await loadAll();
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Помилка збереження', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PurchasesNav />
      <Typography variant="h4" sx={{ mb: 0.5 }}>Журнал постачальників</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Перелік матеріалів з прив'язкою до постачальників. Редагування тут синхронізується з картками постачальників.
      </Typography>

      {/* Filters */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {CATEGORIES.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setCatFilter(cat)}
              color={catFilter === cat ? 'primary' : 'default'}
              variant={catFilter === cat ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder="Пошук по матеріалу…"
            value={matSearch}
            onChange={(e) => setMatSearch(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            size="small"
            placeholder="Пошук по постачальнику…"
            value={supSearch}
            onChange={(e) => setSupSearch(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {filtered.length} позицій
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Категорія</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Назва у постачальника</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Внутрішня назва</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Постачальник(и)</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>Нічого не знайдено</Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(({ mat, category }) => {
                  const key = `${category}:${mat.id}`;
                  const linked = supplierByMat[key] || [];
                  return (
                    <TableRow key={key} hover>
                      <TableCell>
                        <Chip label={category} size="small" variant="outlined" color={CAT_COLOR[category] || 'default'} />
                      </TableCell>
                      <TableCell>{mat.supplier_name || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{mat.name || '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {linked.length === 0
                            ? <Typography variant="body2" color="text.disabled">не вказано</Typography>
                            : linked.map((s) => (
                                <Chip key={s.id} label={s.name} size="small" />
                              ))
                          }
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openEdit(mat, category)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Редагувати матеріал
          {editItem && (
            <Chip
              label={editItem.category}
              size="small"
              color={CAT_COLOR[editItem.category] || 'default'}
              sx={{ ml: 1 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Назва у постачальника"
              fullWidth
              value={editForm.supplier_name}
              onChange={(e) => setEditForm((p) => ({ ...p, supplier_name: e.target.value }))}
            />
            <TextField
              label="Внутрішня назва"
              fullWidth
              value={editForm.name}
              onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
            />
            <Autocomplete
              multiple
              options={suppliers}
              getOptionLabel={(s) => s.name}
              value={editSuppliers}
              onChange={(_, v) => setEditSuppliers(v)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Постачальники" placeholder="Оберіть зі списку…" />}
              renderTags={(val, getTagProps) =>
                val.map((s, i) => <Chip key={s.id} label={s.name} {...getTagProps({ index: i })} size="small" />)
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SupplierJournalPage;
